import type { MatrixQuarterlyReport } from "epsm-api-types/models";
import type { PriceInfo } from "epsm-api-types/ws";
import type { UTCTimestamp } from "lightweight-charts";
import type {
	IContext,
	PineJS,
	StudyMetaInfo,
} from "@/charting_library/charting_library";
import { convertQuarterToDaily } from "@/components/widgets/Chart/helpers/convertQuarterToDaily/convertQuarterToDaily";
import { filterDataByMainSeries } from "@/components/widgets/Chart/helpers/filterDataByMainSeries/filterDataByMainSeries";
import { EmQuarterlyReportService } from "@/components/widgets/Chart/services/EmQuarterlyReportService";
import { TIMEFRAME_MAP } from "@/components/widgets/Chart/services/PriceService/PriceService";
import type { Timeframes } from "@/components/widgets/Chart/types";
import { ONE_MONTH_IN_MS } from "@/lib/utils";
import { CustomIndicatorService } from "../CustomIndicatorService";

export const ONE_MILLION = 1000000;
export abstract class QuarterlyReportIndicatorService extends CustomIndicatorService<MatrixQuarterlyReport> {
	abstract fieldMapper(item: MatrixQuarterlyReport): PriceInfo<UTCTimestamp>;
	abstract get requiredFields(): string[];
	abstract getPlotsData(PineJS: PineJS, context: IContext): number[];
	abstract get styles(): StudyMetaInfo["styles"];

	async getBarData(
		from: number,
		to: number,
		interval: Timeframes,
		ticker: string,
	): Promise<PriceInfo<UTCTimestamp>[]> {
		if (TIMEFRAME_MAP[interval].isIntraday) {
			return [];
		}

		const response = await EmQuarterlyReportService.create().getPriceReports(
			{
				filter: [
					{
						name: "quarter_end_date",
						condition: "gte",
						value: from - 4 * ONE_MONTH_IN_MS,
					},
					{
						name: "quarter_end_date",
						condition: "lt",
						value: to + 4 * ONE_MONTH_IN_MS,
					},
					{
						name: "ticker",
						condition: "eq",
						value: ticker,
					},
				],
				sort: [{ name: "quarter_end_date", order: 1 }],
				fields: ["earning_date", ...this.requiredFields],
			},
			this.fieldMapper,
		);

		const dailyData = convertQuarterToDaily(response.data ?? [], from, to);
		const filteredResponse = await filterDataByMainSeries(
			dailyData,
			from,
			to,
			ticker,
			interval,
		);

		return filteredResponse;
	}

	get plots(): StudyMetaInfo["plots"] {
		return [
			{
				id: "plot_0",
				type: "line",
			},
		] as StudyMetaInfo["plots"];
	}

	get defaults(): StudyMetaInfo["defaults"] {
		return {
			styles: {
				plot_0: {
					plottype: 0,
					linestyle: 0,
					visible: true,
					transparency: 0,
				},
			},
		};
	}
}
